import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, Pencil } from "lucide-react"
import { useTranslation } from "react-i18next"
import { PolarArea, Radar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  RadialLinearScale,
  ArcElement,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  Filler,
} from "chart.js"

// Register ChartJS components
ChartJS.register(
  RadialLinearScale,
  ArcElement,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  Filler,
)

import AppLayout from "@/layouts/app-layout"
import { samplesPath, samplePath, editSamplePath, cuppingPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Info } from "lucide-react"

interface Tasting {
  id: number
  title: string
}

interface Sample {
  id: number
  name: string
  picture: string
  picture_url: string | null
  description: string
  origin: string
  land: string
  producer: string
  process: string
  variety: string
  product_type: string
  tasting?: Tasting
  cupping_data?: {
    fragrance_score: number
    acidity_score: number
    body_score: number
    flavor_score: number
    aftertaste_score: number
    balance_score: number
    overall_score: number
  }
}

const processLabels: Record<string, string> = {
  natural: "Natural",
  washed: "Lavado",
  honey: "Honey",
}

const varietalLabels: Record<string, string> = {
  typica: "Typica",
  caturra: "Caturra",
  catuai: "Catuaí",
  gesha: "Gesha",
  bourbon_amarillo: "Bourbon Amarillo",
  bourbon_rosado: "Bourbon Rosado",
  solok: "Solok",
}

interface Props {
  sample: Sample
}

export default function ShowStandaloneSample({ sample }: Props) {
  const { t } = useTranslation()

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("samples.title"),
      href: samplesPath(),
    },
    {
      title: sample.name,
      href: samplePath(sample.id),
    },
  ]

  const hasCuppingData =
    sample.cupping_data && Object.values(sample.cupping_data).some((v) => v > 0)

  // Flavor wheel data (Polar Area Chart)
  const flavorWheelData = {
    labels: [
      t("samples.show.chart_labels.fragrance"),
      t("samples.show.chart_labels.acidity"),
      t("samples.show.chart_labels.body"),
      t("samples.show.chart_labels.flavor"),
      t("samples.show.chart_labels.aftertaste"),
      t("samples.show.chart_labels.balance"),
      t("samples.show.chart_labels.overall"),
    ],
    datasets: [
      {
        label: t("samples.show.chart_labels.score"),
        data: hasCuppingData
          ? [
              sample.cupping_data!.fragrance_score,
              sample.cupping_data!.acidity_score,
              sample.cupping_data!.body_score,
              sample.cupping_data!.flavor_score,
              sample.cupping_data!.aftertaste_score,
              sample.cupping_data!.balance_score,
              sample.cupping_data!.overall_score,
            ]
          : [0, 0, 0, 0, 0, 0, 0],
        backgroundColor: [
          "rgba(147, 51, 234, 0.6)",
          "rgba(236, 72, 153, 0.6)",
          "rgba(251, 146, 60, 0.6)",
          "rgba(34, 197, 94, 0.6)",
          "rgba(59, 130, 246, 0.6)",
          "rgba(168, 85, 247, 0.6)",
          "rgba(249, 115, 22, 0.6)",
        ],
      },
    ],
  }

  // Spider chart data (Radar Chart)
  const spiderChartData = {
    labels: [
      t("samples.show.chart_labels.fragrance_aroma"),
      t("samples.show.chart_labels.flavor"),
      t("samples.show.chart_labels.aftertaste"),
      t("samples.show.chart_labels.acidity"),
      t("samples.show.chart_labels.body"),
      t("samples.show.chart_labels.balance"),
      t("samples.show.chart_labels.overall"),
    ],
    datasets: [
      {
        label: t("samples.show.chart_labels.cupping_profile"),
        data: hasCuppingData
          ? [
              sample.cupping_data!.fragrance_score,
              sample.cupping_data!.flavor_score,
              sample.cupping_data!.aftertaste_score,
              sample.cupping_data!.acidity_score,
              sample.cupping_data!.body_score,
              sample.cupping_data!.balance_score,
              sample.cupping_data!.overall_score,
            ]
          : [0, 0, 0, 0, 0, 0, 0],
        backgroundColor: "rgba(147, 51, 234, 0.2)",
        borderColor: "rgba(147, 51, 234, 1)",
        borderWidth: 2,
        pointBackgroundColor: "rgba(147, 51, 234, 1)",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "rgba(147, 51, 234, 1)",
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 10,
        ticks: {
          stepSize: 2,
        },
      },
    },
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={sample.name} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Link
            href={samplesPath()}
            className="hover:text-foreground flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("samples.show.back")}
          </Link>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">
              {t("samples.show.page_title")}
            </h1>
            <p className="text-primary mt-1 text-xl font-medium">
              #{String(sample.id).padStart(3, "0")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link href={editSamplePath(sample.id)}>
              <Button
                variant="default"
                className="bg-primary hover:bg-primary/90"
              >
                <Pencil className="mr-2 h-4 w-4" />
                {t("samples.show.edit")}
              </Button>
            </Link>
            {hasCuppingData && (
              <Button
                variant="default"
                className="bg-primary hover:bg-primary/90"
              >
                {t("samples.show.generate_report")}
              </Button>
            )}
          </div>
        </div>

        {/* Charts Section - Only show if cupping has been performed */}
        {hasCuppingData && (
          <Card>
            <CardContent className="p-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Flavor Wheel */}
                <div className="flex flex-col items-center justify-center p-4">
                  <div className="w-full max-w-sm">
                    <PolarArea data={flavorWheelData} options={chartOptions} />
                  </div>
                </div>

                {/* Spider Chart */}
                <div className="flex flex-col items-center justify-center p-4">
                  <div className="w-full max-w-sm">
                    <Radar data={spiderChartData} options={chartOptions} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sample Information */}
        <div>
          <div className="mb-4 flex items-center gap-2">
            <h2 className="text-2xl font-bold">
              {t("samples.show.sample_info")}
            </h2>
            <Link href={editSamplePath(sample.id)}>
              <Button
                variant="default"
                size="icon"
                className="bg-primary hover:bg-primary/90 h-8 w-8"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid gap-6 md:grid-cols-[200px_1fr]">
            {/* Picture */}
            <div>
              {sample.picture_url ? (
                <img
                  src={sample.picture_url}
                  alt={sample.name}
                  className="h-48 w-full rounded-lg border object-cover"
                />
              ) : (
                <div className="bg-muted flex h-48 w-full items-center justify-center rounded-lg">
                  <p className="text-muted-foreground text-sm">
                    {t("samples.show.no_image")}
                  </p>
                </div>
              )}
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Card className="bg-primary/10">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Info className="text-primary mt-1 h-5 w-5" />
                    <div>
                      <p className="text-muted-foreground text-sm font-medium">
                        {t("samples.show.fields.id")}:
                      </p>
                      <p className="text-base font-medium">
                        #{String(sample.id).padStart(3, "0")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-primary/10">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Info className="text-primary mt-1 h-5 w-5" />
                    <div>
                      <p className="text-muted-foreground text-sm font-medium">
                        {t("samples.show.fields.name")}:
                      </p>
                      <p className="text-base font-medium">
                        {sample.name || "-"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-primary/10">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Info className="text-primary mt-1 h-5 w-5" />
                    <div>
                      <p className="text-muted-foreground text-sm font-medium">
                        {t("samples.show.fields.origin")}:
                      </p>
                      <p className="text-base font-medium">
                        {sample.origin || "-"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-primary/10">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Info className="text-primary mt-1 h-5 w-5" />
                    <div>
                      <p className="text-muted-foreground text-sm font-medium">
                        {t("samples.show.fields.land")}:
                      </p>
                      <p className="text-base font-medium">
                        {sample.land || "-"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-primary/10">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Info className="text-primary mt-1 h-5 w-5" />
                    <div>
                      <p className="text-muted-foreground text-sm font-medium">
                        {t("samples.show.fields.producer")}:
                      </p>
                      <p className="text-base font-medium">
                        {sample.producer || "-"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-primary/10">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Info className="text-primary mt-1 h-5 w-5" />
                    <div>
                      <p className="text-muted-foreground text-sm font-medium">
                        {t("samples.show.fields.process")}:
                      </p>
                      <p className="text-base font-medium">
                        {processLabels[sample.process] || sample.process || "-"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-primary/10">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Info className="text-primary mt-1 h-5 w-5" />
                    <div>
                      <p className="text-muted-foreground text-sm font-medium">
                        {t("samples.show.fields.variety")}:
                      </p>
                      <p className="text-base font-medium">
                        {varietalLabels[sample.variety] ||
                          sample.variety ||
                          "-"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {sample.tasting && (
                <Card className="bg-primary/10">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2">
                      <Info className="text-primary mt-1 h-5 w-5" />
                      <div>
                        <p className="text-muted-foreground text-sm font-medium">
                          {t("samples.show.fields.cupping")}:
                        </p>
                        <Link
                          href={cuppingPath(sample.tasting.id)}
                          className="text-primary text-base font-medium hover:underline"
                        >
                          {sample.tasting.title}
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Description */}
          {sample.description && (
            <Card className="mt-4">
              <CardContent className="p-4">
                <p className="text-muted-foreground mb-2 text-sm font-medium">
                  {t("samples.show.fields.description")}:
                </p>
                <p className="text-base">{sample.description}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
